import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtUSD, fmtNum } from "../../lib/api";
import { countdownFor } from "../../lib/dealMeta";

const badge = (item) => {
  const c = item.commitment;
  if (c && ["allocated", "funds_sent", "settled"].includes(c.status)) return { label: "Allocated", cls: "text-bone" };
  if (c) return { label: "Committed", cls: "text-bone" };
  if (item.response === "interested") return { label: "Interested", cls: "text-bone" };
  return { label: "Passed", cls: "text-muted-s" };
};

export default function Interests() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/deals/my-interests").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!items) return <span className="t-label">Loading</span>;

  const interested = items.filter((i) => i.response === "interested");
  const passed = items.filter((i) => i.response === "pass");
  const allocated = items.filter((i) => i.commitment && ["allocated", "funds_sent", "settled"].includes(i.commitment.status));

  return (
    <div className="rise" data-testid="interests-page">
      <span className="t-label">Interest</span>
      <h1 className="t-h1 text-bone mt-3">Your deal interest</h1>
      <p className="t-small text-muted-s mt-2 measure">
        Every allocation you have weighed in on, with your place in line and where it stands. We
        generally allocate in the order interest was registered.
      </p>

      <div className="grid grid-cols-3 gap-8 mt-10 max-w-md" data-testid="interest-summary">
        {[
          ["Interested", fmtNum(interested.length)],
          ["Allocated", fmtNum(allocated.length)],
          ["Passed", fmtNum(passed.length)],
        ].map(([k, v]) => (
          <div key={k} className="hairline pt-4">
            <span className="t-label">{k}</span>
            <div className="fig text-bone text-2xl mt-2" data-testid={`interest-count-${k.toLowerCase()}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-12 space-y-0">
        {items.map((item) => {
          const b = badge(item);
          const closed = item.deal_status !== "open";
          const committed = !!item.commitment;
          const cd = countdownFor(item.deal_status, item.closes_date, item.closes_at);
          return (
            <div key={item.deal_id} className="hairline py-6 flex flex-wrap gap-x-10 gap-y-4 items-baseline justify-between"
              data-testid={`interest-card-${item.deal_id}`}>
              <div className="min-w-[240px]">
                <Link to={`/app/deals/${item.deal_id}`} className="t-body text-bone hover:underline underline-offset-4"
                  data-testid={`interest-company-${item.deal_id}`}>{item.company}</Link>
                <div className="t-cap text-muted-s mt-1">{item.sector} · {item.region} · {item.stage}</div>
              </div>

              <div className="t-small text-muted-s min-w-[140px]">{item.round_label}</div>

              <div className="min-w-[200px]">
                {item.response === "interested" && item.position ? (
                  <>
                    <span className="fig t-small text-bone" data-testid={`interest-position-${item.deal_id}`}>
                      Number {item.position} of {item.total_interested}
                    </span>
                    {item.demand && (
                      <div className="mt-2 max-w-[220px]" data-testid={`interest-demand-${item.deal_id}`}>
                        <div className="h-1.5 bg-[#14171A] border border-[#24272B] overflow-hidden">
                          <div className="h-full bg-[#E8E6E1]" style={{ width: `${Math.min(100, item.demand.pct)}%` }} />
                        </div>
                        <span className={`t-cap ${item.demand.pct >= 100 ? "text-bone" : "text-muted-s"}`}>
                          {item.demand.pct}% of target{item.demand.pct >= 100 ? ", oversubscribed" : ""}
                        </span>
                      </div>
                    )}
                  </>
                ) : item.response === "pass" ? (
                  <span className="t-small text-muted-s">You passed</span>
                ) : (
                  <span className="t-small text-muted-s">On the waitlist</span>
                )}
              </div>

              <div className="min-w-[120px]">
                <span className={`t-cap ${cd.urgent ? "text-bone" : "text-muted-s"}`} data-testid={`interest-countdown-${item.deal_id}`}>{cd.text}</span>
              </div>

              <div className="flex items-center gap-6 min-w-[200px] justify-end">
                <span className={`t-label ${b.cls}`} data-testid={`interest-status-${item.deal_id}`}>{b.label}</span>
                {committed ? (
                  <Link to={`/app/deals/${item.deal_id}`} className="t-label hover:!text-[#E8E6E1]"
                    data-testid={`interest-view-${item.deal_id}`}>View</Link>
                ) : closed ? null : item.response === "interested" ? (
                  <Link to={`/app/deals/${item.deal_id}`} className="btn-primary !py-2 whitespace-nowrap"
                    data-testid={`interest-commit-${item.deal_id}`}>Review and commit</Link>
                ) : (
                  <Link to={`/app/deals/${item.deal_id}`} className="btn-ghost !py-2 whitespace-nowrap"
                    data-testid={`interest-reconsider-${item.deal_id}`}>Reconsider</Link>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="t-small text-muted-s hairline py-8" data-testid="interest-empty">
            You have not registered interest on any deals yet. Open a memo under Deals and mark
            whether you are interested.
          </p>
        )}
      </div>
    </div>
  );
}
